/**
 * BScope - a kill-team brother's Squad lane (plan modes-rework, stage 6; the auspex and rail before it): a radar's
 * B-scope filling the lane - bearing across (up the map in the middle), distance down (his support range at the
 * bottom), range marks every 10 m, his own sigil at the top - and one beat runs it: the sweep goes down the scope and
 * each brother's sigil flares as it passes. One beyond the range sits on the bottom edge, hollow. Brothers it cannot
 * place are named under it. The target panel's is the calm one: no start pulse.
 *
 * Geometry is `engine/plate/bscope`; drawn in the lane's frame units, in the team's livery.
 */
import { memo, type CSSProperties } from 'react';
import type { BScope as Geometry } from '../../../../engine/plate/bscope';
import styles from '../../../../styles/PlateKillTeam.module.css';

interface Props {
  geometry: Geometry;
  /** Frame units per screen px: the words and dots keep their screen size. */
  unit: number;
  /** The two-second start just began here (an entry, a start, a join): the scope boots once. */
  starting: boolean;
}

/** The scope's panel, the line up the map, the range marks and their metres. */
function Grid({ g, unit }: { g: Geometry; unit: number }) {
  const { x0, x1, y0, y1 } = g.box;
  const mid = (x0 + x1) / 2;
  return (
    <g>
      <rect className={styles.disc} x={x0} y={y0} width={x1 - x0} height={y1 - y0} />
      <line className={styles.range} x1={mid} y1={y0} x2={mid} y2={y1} />
      {g.marks.map((m) => (
        <g key={m.m}>
          <line className={styles.tick} x1={x0} y1={m.y} x2={x1} y2={m.y} />
          <text className={styles.tape} x={x1 - 2 * unit} y={m.y - 1.5 * unit} textAnchor="end" fontSize={7 * unit}>
            {`${m.m}`}
          </text>
        </g>
      ))}
    </g>
  );
}

/** His own sigil at the top, each brother's where the scope places him, and the sweep that lights them. */
function Sigils({ g, unit }: { g: Geometry; unit: number }) {
  const { x0, x1, y0, y1 } = g.box;
  const dot = 2.4 * unit;
  const mid = (x0 + x1) / 2;
  return (
    <g>
      <path
        d={`M${mid} ${y0} l${dot} ${dot * 2.6} l${-dot} ${-dot * 0.8} l${-dot} ${dot * 0.8} z`}
        fill="currentColor"
      />
      {g.blips.map((b) => (
        <circle
          key={b.id}
          className={b.beyond ? styles.sigilBeyond : styles.sigil}
          data-testid={`plate-bscope-blip-${b.id}`}
          cx={b.x}
          cy={b.y}
          r={dot}
          style={{ animationDelay: `${b.delay * g.beat}s` }}
        />
      ))}
      <line
        className={styles.sweep}
        x1={x0}
        y1={y0}
        x2={x1}
        y2={y0}
        style={{ '--run': `${y1 - y0}px` } as CSSProperties}
      />
    </g>
  );
}

export default memo(function BScope({ geometry: g, unit, starting }: Props) {
  return (
    <g className={styles.bscope} data-testid="plate-bscope" style={{ '--beat': `${g.beat}s` } as CSSProperties}>
      <g className={starting ? styles.starting : undefined}>
        <Grid g={g} unit={unit} />
        {/* One clock for the sweep and the sigils: a brother placed anew restarts both, never out of step. */}
        <Sigils key={g.blips.map((b) => b.id).join(',')} g={g} unit={unit} />
      </g>
      {g.absent.length > 0 && (
        <text
          className={styles.header}
          x={(g.box.x0 + g.box.x1) / 2}
          y={g.box.y1 + 9 * unit}
          textAnchor="middle"
          fontSize={7 * unit}
          data-testid="plate-bscope-absent"
        >
          {`+ ${g.absent.join(', ')}`}
        </text>
      )}
    </g>
  );
});
